import type { JsonValue } from 'trystero';
import { APP_ID, passwordFor, roomIdFor } from './protocol';
import type { TransportFactory } from './Session';

/**
 * Peer-to-peer transport over WebRTC. GitHub Pages can't run a game server, so players
 * connect directly to each other; public Nostr relays are only used to introduce them
 * (and that handshake is encrypted with the park code).
 *
 * The library is loaded on first use, so single-player never downloads it.
 */
export const trysteroTransport: TransportFactory = async (code, onProblem) => {
  const { joinRoom } = await import('trystero');
  const room = joinRoom({ appId: APP_ID, password: passwordFor(code) }, roomIdFor(code), {
    onJoinError: ({ error, peerId }) =>
      onProblem(error.includes('password') ? 'password' : 'unreachable', peerId),
  });
  return {
    channel: (name, onMessage) => {
      // All payloads are plain JSON-safe data built by the protocol module.
      const action = room.makeAction<JsonValue>(name, {
        onMessage: (data, { peerId }) => onMessage(data, peerId),
      });
      return (data, peerId) => {
        action.send(data as JsonValue, peerId ? { target: peerId } : undefined).catch(() => undefined);
      };
    },
    onPeerJoin: (fn) => {
      room.onPeerJoin = fn;
    },
    onPeerLeave: (fn) => {
      room.onPeerLeave = fn;
    },
    leave: () => {
      room.leave().catch(() => undefined);
    },
  };
};
